import { useMemo, useState } from 'react';
import { csvName } from '@/lib/csv';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { date, gradeLabel, money } from '@/lib/format';
import { adminLinks } from '@/lib/links';
import { ErrorNote, Spinner } from '@/components/Layout';
import { downloadCsv } from './AdminLayout';
import { useCanEdit } from '@/lib/queries';

interface Row {
  id: string; graded_on: string; for_rank: number; certificate: string; certificate_ordered_on: string | null;
  person: { id: string; first_name: string; last_name: string; dojo: { id: string; short_name: string | null; name: string } | null } | null;
  event: { id: string; title: string } | null;
}
const FEE_PENCE = 150; // £1.50 per certificate (EGKA certificate application form)

/** Passes waiting for certificates, grouped by dojo, in the layout of EGKA's certificate application form. */
export function AdminCertificates() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [show, setShow] = useState<'to_order' | 'ordered'>('to_order');
  const [error, setError] = useState('');
  const q = useQuery({
    queryKey: ['admin-certificates', show],
    queryFn: async () => {
      const { data, error } = await supabase.from('grading').select('id,graded_on,for_rank,certificate,certificate_ordered_on,person:person_id(id,first_name,last_name,dojo:home_dojo_id(id,short_name,name)),event:event_id(id,title)')
        .eq('result', 'passed').eq('certificate', show).order('graded_on');
      if (error) throw error;
      return data as unknown as Row[];
    },
  });
  const byDojo = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of q.data ?? []) { const k = r.person?.dojo?.short_name ?? r.person?.dojo?.name ?? 'No dojo'; m.set(k, [...(m.get(k) ?? []), r]); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [q.data]);

  async function mark(ids: string[], certificate: 'ordered' | 'sent') {
    setError('');
    const { error } = await supabase.from('grading').update({ certificate }).in('id', ids);
    if (error) setError(error.message);
    await qc.invalidateQueries({ queryKey: ['admin-certificates'] });
  }
  const csv = (rows: Row[], dojo: string) => downloadCsv(csvName('Certificates', [dojo, show === 'to_order' ? 'To order' : 'Ordered']), [
    ['Dojo', 'Grading date', "Student's first initial and last name", 'Grade awarded'],
    ...rows.map(r => [dojo, date(r.graded_on), `${r.person?.first_name?.[0] ?? ''}. ${r.person?.last_name ?? ''}`, gradeLabel(r.for_rank)]),
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">Everyone who has passed a grading. Download a dojo's list in the certificate form's layout, then mark it ordered.</p>
        <select className="input w-auto" aria-label="Show" value={show} onChange={e => setShow(e.target.value as typeof show)}><option value="to_order">To order</option><option value="ordered">Ordered, not yet sent</option></select>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {q.isLoading ? <Spinner /> : !byDojo.length ? <p className="card text-sm text-ink-500">{show === 'to_order' ? 'No certificates waiting.' : 'Nothing waiting to be sent.'}</p> : byDojo.map(([dojo, rows]) => (
        <section key={dojo} className="card space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">{rows[0]?.person?.dojo ? <Link to={adminLinks.dojo(rows[0].person.dojo.id)} className="hover:underline">{dojo}</Link> : dojo} <span className="font-normal text-ink-500">({rows.length}, {money(rows.length * FEE_PENCE)})</span></h3>
            <span className="flex gap-2">
              <button className="btn-ghost border border-stone-300 text-sm" onClick={() => csv(rows, dojo)}><Download size={15} /> Download</button>
              {canEdit && <button className="btn-primary text-sm" onClick={() => mark(rows.map(r => r.id), show === 'to_order' ? 'ordered' : 'sent')}>{show === 'to_order' ? 'Mark all ordered' : 'Mark all sent'}</button>}
            </span>
          </div>
          <table className="w-full text-left text-sm">
            <thead className="text-ink-500"><tr><th className="py-1 pr-3 font-medium">Grading date</th><th className="pr-3 font-medium">Student</th><th className="pr-3 font-medium">Grade awarded</th><th className="font-medium">Grading</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="py-2 pr-3">{date(r.graded_on)}</td>
                  <td className="pr-3">{r.person ? <Link to={adminLinks.person(r.person.id)} className="link">{r.person.first_name} {r.person.last_name}</Link> : null}</td>
                  <td className="pr-3">{gradeLabel(r.for_rank)}</td>
                  <td>{r.event ? <Link to={adminLinks.event(r.event.id)} className="hover:underline">{r.event.title}</Link> : '—'}{r.certificate_ordered_on && <span className="block text-xs text-ink-500">Ordered {date(r.certificate_ordered_on)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
